import React, { useState, useEffect } from "react";
import {
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Spin,
  message,
  Card,
  Row,
  Col,
  Statistic,
  Descriptions,
  Modal,
  Divider,
  Image,
  List,
  Alert,
} from "antd";
import {
  EyeOutlined,
  CalendarOutlined,
  ShoppingCartOutlined,
  DollarCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
  UserOutlined,
  ShopOutlined,
  InboxOutlined,
  CalculatorOutlined,
} from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;

const UserOrders = ({ userId }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailsModal, setOrderDetailsModal] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [orderStats, setOrderStats] = useState({
    totalOrders: 0,
    totalAmount: 0,
    pendingOrders: 0,
    completedOrders: 0,
  });

  useEffect(() => {
    if (userId) {
      fetchOrders();
    }
  }, [userId]);

  const fetchOrders = async () => {
    setLoading(true);
    const tokenString = localStorage.getItem("token");
    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL +
          `api/Order/GetOrderListByUserIDforWeb?UserID=${userId}`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
      };

      const response = await axios.request(config);
      setOrders(response.data);
      calculateOrderStats(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
      message.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetails = async (orderId) => {
    setOrderDetailsLoading(true);
    const tokenString = localStorage.getItem("token");
    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL +
          `api/Order/GetOrderDetailByIDforSW?OrderID=${orderId}`,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
      };

      const response = await axios.request(config);
      setOrderDetails(response.data);
    } catch (error) {
      console.error("Error fetching order details:", error);
      message.error("Failed to fetch order details");
    } finally {
      setOrderDetailsLoading(false);
    }
  };

  const calculateOrderStats = (orderData) => {
    const stats = {
      totalOrders: orderData.length,
      // Updated to use GrandTotal instead of TotalAmount
      totalAmount: orderData.reduce(
        (sum, order) => sum + (order.GrandTotal || 0),
        0
      ),
      // Note: Status fields are not in the API response, so these will be 0
      pendingOrders: orderData.filter((order) => order.Status === "Pending")
        .length,
      completedOrders: orderData.filter((order) => order.Status === "Completed")
        .length,
    };
    setOrderStats(stats);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";

      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";

      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";

      hours = hours % 12;
      hours = hours ? hours : 12;
      const formattedHours = hours.toString().padStart(2, "0");

      return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
    } catch (error) {
      return "Invalid Date";
    }
  };

  const handleViewOrder = async (order) => {
    setSelectedOrder(order);
    setOrderDetailsModal(true);
    setOrderDetails(null); // Reset previous details
    await fetchOrderDetails(order.OrderID);
  };

  // Updated order columns to match API response fields
  const orderColumns = [
    {
      title: "Order ID",
      dataIndex: "OrderID",
      key: "OrderID",
      render: (orderId) => (
        <Text strong style={{ color: "#1890ff" }}>
          #{orderId}
        </Text>
      ),
      width: "10%",
    },
    {
      title: "Customer Name",
      dataIndex: "CustomerName",
      key: "CustomerName",
      render: (name) => (
        <Text strong style={{ fontSize: "13px" }}>
          {name || "N/A"}
        </Text>
      ),
      width: "15%",
    },
    {
      title: "Firm Name",
      dataIndex: "FirmName",
      key: "FirmName",
      render: (firmName) => (
        <Text style={{ fontSize: "12px" }}>{firmName || "N/A"}</Text>
      ),
      width: "15%",
    },
    {
      title: "Contact",
      dataIndex: "ContactNo",
      key: "ContactNo",
      render: (contact) => (
        <Text style={{ fontSize: "12px" }}>
          <PhoneOutlined style={{ marginRight: 4 }} />
          {contact || "N/A"}
        </Text>
      ),
      width: "12%",
    },
    {
      title: "Order Date",
      dataIndex: "OrderDate",
      key: "OrderDate",
      render: (date) => (
        <Text style={{ fontSize: "12px" }}>{formatDate(date)}</Text>
      ),
      width: "12%",
      sorter: (a, b) => new Date(b.OrderDate) - new Date(a.OrderDate),
      defaultSortOrder: "descend",
    },
    {
      title: "Boxes",
      dataIndex: "TotalBox",
      key: "TotalBox",
      render: (boxes) => (
        <Tag color="blue" style={{ fontSize: "11px" }}>
          {boxes || 0} Boxes
        </Tag>
      ),
      width: "8%",
      align: "center",
    },
    {
      title: "Quantity",
      dataIndex: "TotalQuantity",
      key: "TotalQuantity",
      render: (quantity) => (
        <Tag color="purple" style={{ fontSize: "11px" }}>
          {quantity || 0} pcs
        </Tag>
      ),
      width: "10%",
      align: "center",
    },
    {
      title: "Grand Total",
      dataIndex: "GrandTotal",
      key: "GrandTotal",
      render: (amount) => (
        <Text strong style={{ color: "#52c41a", fontSize: "13px" }}>
          ₹{amount?.toLocaleString("en-IN") || "0.00"}
        </Text>
      ),
      width: "12%",
      align: "right",
      sorter: (a, b) => (a.GrandTotal || 0) - (b.GrandTotal || 0),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewOrder(record)}
          size="small"
        >
          View Details
        </Button>
      ),
      width: "10%",
    },
  ];

  // Order details product columns
  const orderDetailColumns = [
    {
      title: "Image",
      dataIndex: "ImageUrl",
      key: "ImageUrl",
      render: (imageUrl) => (
        <Image
          src={imageUrl}
          alt="Product"
          width={50}
          height={50}
          style={{ objectFit: "cover", borderRadius: 4 }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG8QtJGFdgKbvSgbN1oC3KKaLNj8xf5QY5JJYCJdoFLULAw8TUMJPLl7m3lRcGONruTk/1V+9t6HBVrx9v"
        />
      ),
      width: 80,
    },
    {
      title: "Product Details",
      key: "productDetails",
      render: (_, record) => (
        <div>
          <Text strong style={{ fontSize: "14px", display: "block" }}>
            {record.ProductName}
          </Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Design: {record.DesignNo}
          </Text>
          {record.Specification && (
            <div style={{ marginTop: "4px" }}>
              <Text style={{ fontSize: "11px", color: "#666" }}>
                {record.Specification}
              </Text>
            </div>
          )}
          {(record.Color || record.Size) && (
            <div style={{ marginTop: "4px" }}>
              <Space size="small">
                {record.Color && (
                  <Tag color="blue" size="small">
                    {record.Color}
                  </Tag>
                )}
                {record.Size && (
                  <Tag color="green" size="small">
                    {record.Size}
                  </Tag>
                )}
              </Space>
            </div>
          )}
        </div>
      ),
      width: "30%",
    },
    {
      title: "Quantity",
      key: "quantity",
      render: (_, record) => (
        <div style={{ textAlign: "center" }}>
          <Text strong style={{ fontSize: "14px", display: "block" }}>
            {record.NoOfBox} Box
          </Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.BoxSize} pcs/box
          </Text>
          <Text style={{ fontSize: "12px", color: "#1890ff" }}>
            Total: {record.TotalQuantity} pcs
          </Text>
        </div>
      ),
      width: "15%",
      align: "center",
    },
    {
      title: "Price",
      dataIndex: "Price",
      key: "Price",
      render: (price) => (
        <Text strong style={{ color: "#52c41a", fontSize: "14px" }}>
          ₹{price?.toLocaleString("en-IN") || "0.00"}
        </Text>
      ),
      width: "12%",
      align: "right",
    },
    {
      title: "Amount",
      dataIndex: "Amount",
      key: "Amount",
      render: (amount) => (
        <Text strong style={{ color: "#52c41a", fontSize: "14px" }}>
          ₹{amount?.toLocaleString("en-IN") || "0.00"}
        </Text>
      ),
      width: "12%",
      align: "right",
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <div style={{ marginTop: "16px" }}>
          <Text>Loading orders...</Text>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Order Statistics */}
      <Card size="small" style={{ marginBottom: "16px" }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Total Orders"
              value={orderStats.totalOrders}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Amount"
              value={orderStats.totalAmount}
              prefix={<DollarCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
              formatter={(value) => `₹${value.toLocaleString("en-IN")}`}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Boxes"
              value={orders.reduce(
                (sum, order) => sum + (order.TotalBox || 0),
                0
              )}
              prefix={<InboxOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Quantity"
              value={orders.reduce(
                (sum, order) => sum + (order.TotalQuantity || 0),
                0
              )}
              suffix="pcs"
              valueStyle={{ color: "#fa8c16" }}
            />
          </Col>
        </Row>
      </Card>

      {/* Orders Table */}
      <Card>
        <div style={{ marginBottom: "16px" }}>
          <Space>
            <Button
              icon={<SearchOutlined />}
              onClick={fetchOrders}
              loading={loading}
            >
              Refresh Orders
            </Button>
            <Text type="secondary">Total orders: {orders.length}</Text>
          </Space>
        </div>

        <Table
          columns={orderColumns}
          dataSource={orders}
          rowKey="OrderID"
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} orders`,
            pageSizeOptions: ["10", "15", "25", "50"],
          }}
          size="small"
          locale={{
            emptyText: "No orders found for this user",
          }}
          scroll={{ x: 1200 }}
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
        />
      </Card>

      {/* Enhanced Order Details Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ShoppingCartOutlined style={{ color: "#1890ff" }} />
            <span>Order Details - #{selectedOrder?.OrderID}</span>
          </div>
        }
        open={orderDetailsModal}
        onCancel={() => {
          setOrderDetailsModal(false);
          setSelectedOrder(null);
          setOrderDetails(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setOrderDetailsModal(false);
              setSelectedOrder(null);
              setOrderDetails(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={1200}
      >
        {orderDetailsLoading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
            <div style={{ marginTop: "16px" }}>
              <Text>Loading order details...</Text>
            </div>
          </div>
        ) : orderDetails ? (
          <div>
            {/* Customer Information */}
            <Card
              title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <UserOutlined />
                  <span>Customer Information</span>
                </div>
              }
              size="small"
              style={{ marginBottom: "16px" }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Customer Name">
                      <Text strong>{orderDetails.CustomerName}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Firm Name">
                      <Text>{orderDetails.FirmName}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Contact Number">
                      <PhoneOutlined style={{ marginRight: 4 }} />
                      <Text>{orderDetails.ContactNo}</Text>
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col span={12}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Order Date">
                      <CalendarOutlined style={{ marginRight: 4 }} />
                      <Text>{formatDateTime(orderDetails.OrderDate)}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Customer ID">
                      <Text>{orderDetails.CusIDOnLocal}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="User ID">
                      <Text>{orderDetails.UserID}</Text>
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </Card>

            {/* Order Summary */}
            <Card
              title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <CalculatorOutlined />
                  <span>Order Summary</span>
                </div>
              }
              size="small"
              style={{ marginBottom: "16px" }}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Total Boxes"
                    value={orderDetails.TotalBox}
                    prefix={<InboxOutlined />}
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Total Quantity"
                    value={orderDetails.TotalQuantity}
                    suffix="pcs"
                    valueStyle={{ color: "#722ed1" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Grand Total"
                    value={orderDetails.GrandTotal}
                    prefix="₹"
                    valueStyle={{ color: "#52c41a", fontWeight: "bold" }}
                    formatter={(value) => value.toLocaleString("en-IN")}
                  />
                </Col>
              </Row>

              <Divider />

              <Row gutter={16}>
                <Col span={8}>
                  <div style={{ textAlign: "center" }}>
                    <Text type="secondary">Sub Amount</Text>
                    <div style={{ fontSize: "18px", fontWeight: "500" }}>
                      ₹{orderDetails.SubAmount.toLocaleString("en-IN")}
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: "center" }}>
                    <Text type="secondary">GST Amount</Text>
                    <div style={{ fontSize: "18px", fontWeight: "500" }}>
                      ₹{orderDetails.GSTAmount.toLocaleString("en-IN")}
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: "center" }}>
                    <Text type="secondary">Grand Total</Text>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "bold",
                        color: "#52c41a",
                      }}
                    >
                      ₹{orderDetails.GrandTotal.toLocaleString("en-IN")}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* Order Items */}
            <Card
              title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <ShopOutlined />
                  <span>
                    Order Items ({orderDetails.OrderDetail?.length || 0})
                  </span>
                </div>
              }
              size="small"
            >
              <Table
                columns={orderDetailColumns}
                dataSource={orderDetails.OrderDetail || []}
                rowKey="ProductID"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`,
                }}
                size="small"
                locale={{
                  emptyText: "No items found in this order",
                }}
                scroll={{ x: 800 }}
                rowClassName={(record, index) =>
                  index % 2 === 0 ? "table-row-light" : "table-row-dark"
                }
              />
            </Card>
          </div>
        ) : (
          <Alert
            message="No Details Available"
            description="Unable to load order details at this time."
            type="warning"
            showIcon
            icon={<InfoCircleOutlined />}
          />
        )}
      </Modal>
    </div>
  );
};

export default UserOrders;
